import React from 'react';
import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { 
  PlusCircleIcon,
  CheckCircleIcon,
  TrophyIcon,
  AdjustmentsHorizontalIcon,
  InformationCircleIcon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon as CheckCircleIconSolid,
  XCircleIcon as XCircleIconSolid
} from '@heroicons/react/24/solid';
import { Notification, useNotificationStore } from '../../store/notificationStore';
import { getSymbolLogoUrlSync } from '../../utils/symbolUtils';
import { useIsMobile, formatEventTimestamp } from '../../utils/strategyFormatting';

// MarqueeText component for scrolling long text
interface MarqueeTextProps {
  text: string;
  speed?: number; // New prop: pixels per second (default: 40)
}

const MarqueeText: React.FC<MarqueeTextProps> = ({ text, speed = 40 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollAmount, setScrollAmount] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const textElement = container.firstElementChild as HTMLElement;
      if (textElement) {
        const containerWidth = container.offsetWidth;
        const textWidth = textElement.scrollWidth;
        const currentlyOverflowing = textWidth > containerWidth;
        const currentScrollAmount = Math.max(0, textWidth - containerWidth);
        
        if (currentlyOverflowing !== isOverflowing || currentScrollAmount !== scrollAmount) {
          setIsOverflowing(currentlyOverflowing);
          setScrollAmount(currentScrollAmount);
        }
      }
    }
  }, [text, isOverflowing, scrollAmount]);

  const shouldAnimate = isOverflowing;
  const targetX = shouldAnimate ? [0, -scrollAmount, 0] : 0;
  const transitionConfig = shouldAnimate ? {
    x: {
      repeat: Infinity,
      repeatType: 'mirror' as const,
      duration: Math.max(3, scrollAmount / speed), // Use configurable speed, increased min duration
      ease: 'linear' as const,
      repeatDelay: 2, // Increased pause duration for better readability
    },
  } : { duration: 0.2, ease: 'easeOut' };

  return (
    <div 
      ref={containerRef}
      className="overflow-hidden whitespace-nowrap flex-1 min-w-0"
      title={text} // Always show full text in tooltip
    >
      <motion.span
        className="inline-block"
        animate={{ x: targetX }}
        transition={transitionConfig}
      >
        {text}
      </motion.span>
    </div>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
  const { markAsRead, dismissNotification } = useNotificationStore();
  const isMobile = useIsMobile();

  // Get icon component and color based on type and profit/loss
  const getIconDetails = (notification: Notification) => {
    const pnl = notification.metadata?.pnl || notification.metadata?.final_pnl || 0;
    const isProfit = pnl > 0;
    const isLoss = pnl < 0;
    
    switch (notification.type) {
      case 'position_opened':
        return {
          IconComponent: PlusCircleIcon,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20'
        };
      case 'trade_closed':
        if (isProfit) {
          return {
            IconComponent: ArrowTrendingUpIcon,
            color: 'text-green-400',
            bgColor: 'bg-green-500/20'
          };
        } else if (isLoss) {
          return {
            IconComponent: ArrowTrendingDownIcon,
            color: 'text-red-400',
            bgColor: 'bg-red-500/20'
          };
        } else {
          return {
            IconComponent: CheckCircleIcon,
            color: 'text-gray-400',
            bgColor: 'bg-gray-500/20'
          };
        }
      case 'position_closed':
        if (isProfit) {
          return {
            IconComponent: CheckCircleIconSolid,
            color: 'text-green-400',
            bgColor: 'bg-green-500/20'
          };
        } else if (isLoss) {
          return {
            IconComponent: XCircleIconSolid,
            color: 'text-red-400',
            bgColor: 'bg-red-500/20'
          };
        } else {
          return {
            IconComponent: CheckCircleIcon,
            color: 'text-gray-400',
            bgColor: 'bg-gray-500/20'
          };
        }
      case 'milestone':
        const milestone = notification.metadata?.milestone_percentage || 0;
        
        // Enhanced milestone importance color coding
        if (milestone >= 75 || milestone <= -75) {
          // Near target - high importance
          return {
            IconComponent: TrophyIcon,
            color: milestone > 0 ? 'text-green-300' : 'text-red-300',
            bgColor: milestone > 0 ? 'bg-green-400/30' : 'bg-red-400/30'
          };
        } else if (milestone >= 50 || milestone <= -50) {
          // Major milestone - medium importance  
          return {
            IconComponent: TrophyIcon,
            color: milestone > 0 ? 'text-green-400' : 'text-red-400',
            bgColor: milestone > 0 ? 'bg-green-500/25' : 'bg-red-500/25'
          };
        } else if (milestone > 0) {
          // Regular profit milestone
          return {
            IconComponent: TrophyIcon,
            color: 'text-green-400',
            bgColor: 'bg-green-500/20'
          };
        } else if (milestone < 0) {
          // Regular loss milestone
          return {
            IconComponent: TrophyIcon,
            color: 'text-red-400',
            bgColor: 'bg-red-500/20'
          };
        } else {
          // Breakeven
          return {
            IconComponent: TrophyIcon,
            color: 'text-yellow-400',
            bgColor: 'bg-yellow-500/20'
          };
        }
      case 'position_modified':
        return {
          IconComponent: AdjustmentsHorizontalIcon,
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20'
        };
      case 'system':
      default:
        return {
          IconComponent: InformationCircleIcon,
          color: 'text-gray-400',
          bgColor: 'bg-gray-500/20'
        };
    }
  };

  // Get border color for profit/loss indication
  const getBorderColor = (notification: Notification) => {
    const pnl = notification.metadata?.pnl || notification.metadata?.final_pnl || 0;
    
    if (notification.type === 'trade_closed' || notification.type === 'position_closed') {
      if (pnl > 0) return 'border-green-400';
      if (pnl < 0) return 'border-red-400';
    }
    
    if (notification.type === 'milestone') {
      const milestone = notification.metadata?.milestone_percentage || 0;
      if (milestone > 0) return 'border-green-400';
      if (milestone < 0) return 'border-red-400';
      return 'border-yellow-400';
    }
    
    return 'border-blue-400';
  };

  // Get title text color for profit/loss indication
  const getTitleColor = (notification: Notification) => {
    const pnl = notification.metadata?.pnl || notification.metadata?.final_pnl || 0;
    
    if (notification.type === 'trade_closed' || notification.type === 'position_closed') {
      if (pnl > 0) return 'text-green-400';
      if (pnl < 0) return 'text-red-400';
    }
    
    if (notification.type === 'milestone') {
      const milestone = notification.metadata?.milestone_percentage || 0;
      if (milestone > 0) return 'text-green-400';
      if (milestone < 0) return 'text-red-400';
      return 'text-yellow-400';
    }
    
    return !notification.isRead ? 'text-white' : 'text-white/80';
  };

  // Handle click - mark as read and trigger click action
  const handleClick = () => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    onClick();
  };

  // Handle dismiss without triggering click action
  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    dismissNotification(notification.id);
  };

  const iconDetails = getIconDetails(notification);
  const borderColor = getBorderColor(notification);
  const titleColor = getTitleColor(notification);
  const { IconComponent, color, bgColor } = iconDetails;
  
  // 🔥 NEW: Check if notification is from "while away" period
  const isWhileAway = notification.metadata?.isRecoveryEvent || 
                     notification.title?.includes('(While Away)') ||
                     notification.title?.includes('While away');
  
  return (
    <motion.div
      className={`relative ${isMobile ? 'p-5' : 'p-4'} cursor-pointer transition-all duration-200 hover:bg-white/10 touch-manipulation ${
        !notification.isRead ? `bg-blue-500/10 border-l-2 ${borderColor}` : ''
      }`}
      onClick={handleClick}
      whileHover={{ x: 1 }} // Reduced from 2 to 1 to prevent horizontal scroll
      whileTap={{ scale: 0.98 }} // Add tap feedback for mobile
      layout
      style={{ 
        minHeight: isMobile ? '72px' : '60px', // Larger touch target on mobile
        minWidth: isMobile ? '100%' : 'auto'   // Full width on mobile
      }}
    >
      {/* Blue Dot - Absolute positioned top-left */}
      {!notification.isRead && (
        <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-blue-400 z-10" />
      )}
      
      {/* Dismiss Button - Absolute positioned top-right */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded text-white/40 hover:text-white/70 hover:bg-white/20 transition-colors touch-manipulation flex items-center justify-center z-20"
        title="Hide notification"
        style={{ minWidth: '20px', minHeight: '20px' }} // Reduced touch target size
      >
        <XMarkIcon className="w-3 h-3" />
      </button>
        <div className="flex items-start space-x-3">
          {/* Icon Column - Stacked Vertically */}
          <div className="flex flex-col space-y-2 flex-shrink-0 w-7 items-center justify-center self-center">
            {/* Symbol Logo */}
            {notification.metadata?.symbol && (
              <img 
                src={getSymbolLogoUrlSync(notification.metadata.symbol, 'small')}
                alt={notification.metadata.symbol}
                className="w-6 h-6 rounded-full"
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
            )}
            
            {/* Type Icon */}
            <div className={`p-1.5 rounded-lg ${bgColor} ${color}`}>
              <IconComponent className="w-4 h-4" />
            </div>
          </div>
          
          {/* Content - Full width available (absolute positioned elements don't affect layout) */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col">
              <div className="flex-1 min-w-0">
                {/* 🔥 NEW: While Away Indicator */}
                {isWhileAway && (
                  <div className="flex items-center space-x-1 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-400"></div>
                    <span className="text-xs text-orange-300 font-medium">While Away</span>
                  </div>
                )}
                
                <h4 className={`text-sm font-medium ${titleColor}`}>
                  {notification.title}
                </h4>
                <p className={`text-sm mt-0.5 ${
                  !notification.isRead ? 'text-white/80' : 'text-white/60'
                }`}>
                  {notification.message}
                </p>
                
                {/* Enhanced Metadata with Strategy Information - Responsive Layout */}
                <div className="flex items-center justify-between mt-1 text-xs text-white/50 min-w-0">
                  {/* Strategy and Account - Can be truncated */}
                  <div className="flex items-center space-x-2 min-w-0 flex-1 mr-2">
                    {/* Recovery indicator OR Milestone percentage badge */}
                    {notification.metadata?.isRecoveryEvent && notification.type === 'milestone' && notification.metadata?.milestone_percentage !== undefined ? (
                      // For recovery milestone notifications, show percentage badge with proper sign/context
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        notification.metadata.milestone_percentage === 0 
                          ? 'bg-yellow-500/20 text-yellow-300' 
                          : notification.metadata.milestone_percentage > 0 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-red-500/20 text-red-300'
                      }`} 
                            title={`${notification.metadata.milestone_percentage === 0 ? 'Break even' : 
                                   notification.metadata.milestone_percentage > 0 ? 
                                   `${notification.metadata.milestone_percentage}% profit` : 
                                   `${Math.abs(notification.metadata.milestone_percentage)}% loss`} milestone reached while away`}>
                        {notification.metadata.milestone_percentage === 0 ? '0%' : 
                         notification.metadata.milestone_percentage > 0 ? 
                         `+${notification.metadata.milestone_percentage}%` : 
                         `-${Math.abs(notification.metadata.milestone_percentage)}%`}
                      </span>
                    ) : notification.metadata?.isRecoveryEvent ? (
                      // For other recovery notifications, show calendar emoji
                      <span className="text-amber-400 font-medium" title="Event occurred while app was closed">
                        📅
                      </span>
                    ) : notification.type === 'milestone' && notification.metadata?.milestone_percentage !== undefined ? (
                      // For live milestone notifications, show percentage badge with proper sign/context
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        notification.metadata.milestone_percentage === 0 
                          ? 'bg-yellow-500/20 text-yellow-300' 
                          : notification.metadata.milestone_percentage > 0 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-red-500/20 text-red-300'
                      }`} 
                            title={`${notification.metadata.milestone_percentage === 0 ? 'Break even' : 
                                   notification.metadata.milestone_percentage > 0 ? 
                                   `${notification.metadata.milestone_percentage}% profit` : 
                                   `${Math.abs(notification.metadata.milestone_percentage)}% loss`} milestone reached`}>
                        {notification.metadata.milestone_percentage === 0 ? '0%' : 
                         notification.metadata.milestone_percentage > 0 ? 
                         `+${notification.metadata.milestone_percentage}%` : 
                         `-${Math.abs(notification.metadata.milestone_percentage)}%`}
                      </span>
                    ) : null}
                    
                    {notification.metadata?.strategy && (
                      <div className="flex items-center min-w-0 flex-1">
                        <MarqueeText text={notification.metadata.strategy} speed={20} />
                      </div>
                    )}
                    {notification.metadata?.account && notification.metadata.account !== notification.metadata?.strategy && (
                      <>
                        <span>•</span>
                        <div className="flex items-center min-w-0 flex-1">
                          <MarqueeText text={notification.metadata.account} speed={20} />
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Enhanced Timestamp */}
                  <div className="flex-shrink-0">
                    <span>
                      {formatEventTimestamp(
                        notification.metadata?.eventTimestamp,
                        notification.timestamp,
                        notification.metadata?.isRecoveryEvent
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
    </motion.div>
  );
};

export default React.memo(NotificationItem);